import { useCallback, useEffect, useMemo, useState } from 'react';

export const MAX_BULK_LISTS = 200;

/**
 * Selection state for the Lists table: tracks ids, derives header-checkbox
 * tri-state from the currently loaded rows, and enforces the 200-id cap.
 *
 * @param {Array<number>} loadedIds  - ids currently rendered in the table
 * @param {string|number} resetSignal - any value change clears the selection (e.g. filter changes)
 */
export const useListSelection = ({ loadedIds, resetSignal }) => {
  const [selectedRows, setSelectedRows] = useState([]);

  useEffect(() => {
    setSelectedRows([]);
  }, [resetSignal]);

  const selectedSet = useMemo(() => new Set(selectedRows), [selectedRows]);
  const loadedSelectedCount = loadedIds.filter((id) => selectedSet.has(id)).length;
  const allLoadedSelected = loadedIds.length > 0 && loadedSelectedCount === loadedIds.length;
  const someLoadedSelected = loadedSelectedCount > 0 && !allLoadedSelected;

  const toggleAll = useCallback(() => {
    setSelectedRows((prev) => {
      const loadedSet = new Set(loadedIds);
      const selectedFromLoaded = prev.filter((id) => loadedSet.has(id)).length;
      const maxSelectable = Math.min(loadedIds.length, MAX_BULK_LISTS);
      if (selectedFromLoaded >= maxSelectable && maxSelectable > 0) {
        return prev.filter((id) => !loadedSet.has(id));
      }
      return Array.from(new Set([...prev, ...loadedIds])).slice(0, MAX_BULK_LISTS);
    });
  }, [loadedIds]);

  const toggleOne = useCallback((id) => {
    setSelectedRows((prev) => {
      if (prev.includes(id)) return prev.filter((existing) => existing !== id);
      if (prev.length >= MAX_BULK_LISTS) return prev;
      return [...prev, id];
    });
  }, []);

  return {
    selectedRows,
    setSelectedRows,
    selectedSet,
    allLoadedSelected,
    someLoadedSelected,
    toggleAll,
    toggleOne,
  };
};
